// /settings — perfil + sessão + integrações.
// Inclui instruções passo a passo de como conectar Evolution (WhatsApp),
// Resend, Asaas etc. Sem endpoint backend novo — read-only do estado atual.

import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  LogOut,
  Mail,
  MessageCircle,
  Receipt,
  User,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { PillButton } from '../../components/clients/PillButton';
import { PageHelp } from '../../components/common/PageHelp';
import { WhatsappConnectCard } from '../../components/settings/WhatsappConnectCard';
import { useAuthStore } from '../../stores/useAuthStore';

const ROLE_LABEL: Record<string, string> = {
  admin: 'Admin',
  financeiro: 'Financeiro',
  comercial: 'Comercial',
  suporte: 'Suporte',
  dev: 'Dev',
};

export function SettingsPage(): JSX.Element {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  function handleLogout(): void {
    logout();
    toast.success('Sessão encerrada.');
    navigate('/login');
  }

  if (!user) {
    return (
      <div className="space-y-5">
        <h1 className="m-0 text-[26px] font-bold tracking-[-0.025em] text-ink">Configurações</h1>
        <p className="text-[13px] text-muted">Você precisa estar logado pra ver configurações.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-start gap-2">
        <div>
          <h1 className="m-0 text-[26px] font-bold tracking-[-0.025em] text-ink">Configurações</h1>
          <p className="mt-1 text-[13px] text-muted">
            Conta, sessão e integrações ativas neste workspace.
          </p>
        </div>
        <PageHelp id="settings" />
      </header>

      <section className="rounded-[14px] border border-line bg-panel p-5">
        <header className="mb-4 flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-[8px] bg-panel-3 text-muted">
            <User size={13} strokeWidth={1.8} />
          </span>
          <div>
            <h2 className="text-[13px] font-semibold text-ink">Seu perfil</h2>
            <p className="text-[11px] text-muted-2">
              Dados do usuário sincronizados via auth.owlflow
            </p>
          </div>
        </header>

        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-2">
          <DataLine label="Nome" value={user.name || '—'} />
          <DataLine label="E-mail" value={user.email} mono />
          <DataLine label="Função" value={ROLE_LABEL[user.role] ?? user.role} />
          <DataLine label="ID local" value={user.id} mono />
          <DataLine label="Auth ID (owlflow)" value={user.authId} mono />
          <DataLine label="Status" value={user.is_active ? 'Ativo' : 'Desativado'} />
        </dl>

        <div className="mt-5 border-t border-line/40 pt-4">
          <PillButton danger icon={<LogOut size={14} strokeWidth={2} />} onClick={handleLogout}>
            Sair da conta
          </PillButton>
        </div>
      </section>

      {/* Integrações — cards detalhados com passo a passo */}
      <section className="space-y-3">
        <div>
          <h2 className="text-[16px] font-semibold text-ink">Integrações</h2>
          <p className="mt-1 text-[12px] text-muted">
            Como conectar os serviços externos que o Control Center usa.
          </p>
        </div>

        <IntegrationCard
          id="evolution"
          icon={<MessageCircle size={14} strokeWidth={1.8} />}
          title="WhatsApp"
          subtitle="Pra disparar lembretes de cobrança, régua e atendimento por zap"
          defaultOpen
        >
          <WhatsappConnectCard />
        </IntegrationCard>

        <IntegrationCard
          id="resend"
          icon={<Mail size={14} strokeWidth={1.8} />}
          title="E-mail transacional (Resend)"
          subtitle="Pra mandar boletos, notas, lembretes por e-mail"
        >
          <Step n={1} title="Criar conta e domínio no Resend">
            Acesse <ExtLink href="https://resend.com">resend.com</ExtLink>, cria conta gratuita.
            Adiciona seu domínio (ex: <code>opusowl.com.br</code>) e configura os DNS records que
            ela pedir (SPF, DKIM). Sem isso o e-mail cai em spam.
          </Step>
          <Step n={2} title="Gerar API key">
            Resend → API Keys → Create. Copia a key que começa com{' '}
            <code className="font-mono text-[11px]">re_</code>.
          </Step>
          <Step n={3} title="Colocar no .env">
            <pre className="mt-2 overflow-x-auto rounded-[8px] border border-line bg-panel-3 p-3 text-[11px] text-ink-2">
              {`RESEND_API_KEY=re_xxxxxxxxxxx
RESEND_FROM=Opus Owl <no-reply@opusowl.com.br>`}
            </pre>
            <p className="mt-2 text-[12px] text-muted-2">Restart o API. Pronto.</p>
          </Step>
        </IntegrationCard>

        <IntegrationCard
          id="asaas"
          icon={<Wallet size={14} strokeWidth={1.8} />}
          title="Gateway de pagamento (Asaas)"
          subtitle="Cobranças, Pix, boleto, cartão — tudo passa por aqui"
        >
          <Step n={1} title="Conta Asaas">
            Crie/use sua conta no <ExtLink href="https://www.asaas.com">asaas.com</ExtLink>. Pega a
            API key em Configurações → API.
          </Step>
          <Step n={2} title="Cadastrar a conta no Control Center">
            Vai em{' '}
            <a href="/asaas-accounts" className="text-good hover:underline">
              /asaas-accounts
            </a>{' '}
            → Nova conta. Cola o nome (ex: &ldquo;Opus Owl Master&rdquo;), a API key, escolhe
            ambiente (produção/sandbox) e marca como default.
          </Step>
          <Step n={3} title="Configurar webhook no painel Asaas">
            No painel Asaas → Integrações → Notificações por Webhook, aponta a URL pra:
            <pre className="mt-2 overflow-x-auto rounded-[8px] border border-line bg-panel-3 p-3 text-[11px] text-ink-2">
              {`https://central.opusowl.com.br/api/webhooks/asaas/owl-master`}
            </pre>
            <p className="mt-2 text-[12px] text-muted-2">
              O <code>owl-master</code> é o webhookPath da conta no CC. Ative os eventos de
              pagamento (PAYMENT_RECEIVED, PAYMENT_CONFIRMED, PAYMENT_OVERDUE, etc).
            </p>
          </Step>
        </IntegrationCard>

        <IntegrationCard
          id="asaas-notas"
          icon={<Receipt size={14} strokeWidth={1.8} />}
          title="Notas fiscais (Asaas Notas)"
          subtitle="Emissão automática de NFS-e depois que cobrança é paga"
        >
          <Step n={1} title="Habilitar produto Asaas Notas">
            No painel Asaas → Produtos → ative &ldquo;Notas Fiscais&rdquo; (módulo separado, pode
            ter custo).
          </Step>
          <Step n={2} title="Configurar webhook + token global">
            Asaas → Webhooks de Notas. URL:
            <pre className="mt-2 overflow-x-auto rounded-[8px] border border-line bg-panel-3 p-3 text-[11px] text-ink-2">
              {`https://central.opusowl.com.br/api/webhooks/asaas-notas
ASAAS_NOTAS_WEBHOOK_TOKEN=<gere uma string longa, cola aqui e no .env>`}
            </pre>
          </Step>
          <Step n={3} title="Ligar emissão por plano">
            Em{' '}
            <a href="/plans" className="text-good hover:underline">
              /plans
            </a>
            , edita o plano → ativa &ldquo;Emitir NFS-e automaticamente&rdquo;. Toda cobrança paga
            desse plano gera NF sem você fazer nada.
          </Step>
        </IntegrationCard>

        <IntegrationCard
          id="auth"
          icon={<User size={14} strokeWidth={1.8} />}
          title="SSO (auth.owlflow)"
          subtitle="Login compartilhado entre os SaaS Owl"
        >
          <p className="text-[12px] text-muted">
            O auth.owlflow é gerenciado fora do Control Center. Se um usuário do seu time não tem
            acesso, ele precisa ser convidado lá. Sua sessão atual é validada via JWT emitido pelo
            auth.owlflow.
          </p>
        </IntegrationCard>
      </section>
    </div>
  );
}

interface IntegrationCardProps {
  id: string;
  icon: JSX.Element;
  title: string;
  subtitle: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function IntegrationCard({
  id,
  icon,
  title,
  subtitle,
  defaultOpen = false,
  children,
}: IntegrationCardProps): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section id={id} className="overflow-hidden rounded-[14px] border border-line bg-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-5 py-4 text-left hover:bg-panel-2"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-[8px] bg-panel-3 text-muted">
          {icon}
        </span>
        <div className="flex-1">
          <h3 className="m-0 text-[14px] font-semibold text-ink">{title}</h3>
          <p className="mt-[2px] text-[12px] text-muted">{subtitle}</p>
        </div>
        <span className="text-muted">
          {open ? (
            <ChevronDown size={16} strokeWidth={1.8} />
          ) : (
            <ChevronRight size={16} strokeWidth={1.8} />
          )}
        </span>
      </button>
      {open && (
        <div className="space-y-4 border-t border-line bg-panel-2/30 px-5 py-5 text-[13px] leading-relaxed text-ink-2">
          {children}
        </div>
      )}
    </section>
  );
}

function Step({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex gap-3">
      <span className="mt-[2px] flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-good/40 bg-good/10 text-[11px] font-semibold text-good">
        {n}
      </span>
      <div className="flex-1">
        <h4 className="m-0 mb-1 text-[13px] font-semibold text-ink">{title}</h4>
        <div className="text-[12.5px] text-ink-2">{children}</div>
      </div>
    </div>
  );
}

function ExtLink({ href, children }: { href: string; children: React.ReactNode }): JSX.Element {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-good hover:underline"
    >
      {children}
      <ExternalLink size={11} strokeWidth={2} />
    </a>
  );
}

function DataLine({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-[2px] border-b border-line/60 pb-2 last:border-b-0">
      <dt className="text-[11px] uppercase tracking-wider text-muted-3">{label}</dt>
      <dd className={`text-[13px] text-ink-2 ${mono ? 'font-mono text-[12px]' : ''}`}>{value}</dd>
    </div>
  );
}
